import {
    normalizeReviewProduct,
    serializeReviewProduct,
} from '../document-compliance/products/product-attributes'
import { CLAIM_TYPES } from '../document-compliance/regulations/regulation-review-options'

const clone = value => JSON.parse(JSON.stringify(value || {}))

const isObject = value => value !== null && typeof value === 'object' && !Array.isArray(value)

const CONCENTRATION_BASES = Object.freeze(['w/w', 'w/v', 'v/v', 'other'])

const normalizeLegislationClaims = payload => {
    if (!Array.isArray(payload.legislations)) {
        return payload
    }

    payload.legislations = payload.legislations.map(legislation => {
        if (!isObject(legislation) || !Array.isArray(legislation.claims)) {
            return legislation
        }

        return {
            ...legislation,
            claims: legislation.claims
                .filter(claim => isObject(claim) && CLAIM_TYPES.includes(claim.type))
                .map(claim => {
                    const normalized = { ...claim }
                    delete normalized.value
                    return normalized
                }),
        }
    })

    return payload
}

const normalizeConcentrationBases = payload => {
    const assessment = isObject(payload.substanceAssessment)
        ? payload.substanceAssessment
        : null
    const substances = assessment && Array.isArray(assessment.substances)
        ? assessment.substances
        : []

    substances.forEach(substance => {
        if (!substance || !isObject(substance.concentration)) {
            return
        }

        const basis = substance.concentration.basis
        if (basis !== null
            && typeof basis !== 'undefined'
            && !CONCENTRATION_BASES.includes(basis)) {
            substance.concentration.basis = null
        }
    })

    return payload
}

const normalizeProducts = payload => {
    if (!Array.isArray(payload.products)) {
        return payload
    }

    return {
        ...payload,
        products: payload.products.map(normalizeReviewProduct),
    }
}

const ensureObject = (payload, key, fallback = {}) => {
    if (!isObject(payload[key])) {
        payload[key] = clone(fallback)
    }
}

const ensureArray = (payload, key) => {
    if (!Array.isArray(payload[key])) {
        payload[key] = []
    }
}

const resolveEffectiveItemPayload = item => {
    if (!item || typeof item !== 'object') {
        return {}
    }

    if (isObject(item.effectivePayload)) {
        return item.effectivePayload
    }

    if (isObject(item.reviewedPayload)) {
        return item.reviewedPayload
    }

    return isObject(item.rawPayload) ? item.rawPayload : {}
}

const createDocumentComplianceDraft = item => {
    const draft = normalizeProducts(normalizeLegislationClaims(normalizeConcentrationBases(
        clone(resolveEffectiveItemPayload(item)),
    )))

    ensureObject(draft, 'classification', { scope: 'unknown' })
    if (!draft.classification.scope) {
        draft.classification.scope = 'unknown'
    }

    ensureObject(draft, 'documentInformation', { relevance: 'uncertain', type: 'other' })
    if (!draft.documentInformation.relevance) {
        draft.documentInformation.relevance = 'uncertain'
    }
    if (!draft.documentInformation.type) {
        draft.documentInformation.type = 'other'
    }

    ensureArray(draft, 'legislations')
    ensureArray(draft, 'products')
    ensureArray(draft, 'exemptions')
    ensureArray(draft, 'productLinks')
    ensureArray(draft, 'substanceLinks')
    if (!isObject(draft.scipInformation)) {
        draft.scipInformation = null
    }

    return draft
}

const serializeDocumentComplianceDraft = draft => {
    const payload = normalizeLegislationClaims(normalizeConcentrationBases(clone(draft)))
    if (Array.isArray(payload.products)) {
        payload.products = payload.products.map(serializeReviewProduct)
    }
    return payload
}

export {
    createDocumentComplianceDraft,
    CONCENTRATION_BASES,
    resolveEffectiveItemPayload,
    serializeDocumentComplianceDraft,
}
